import { useMemo, useState } from 'react';

import { CalendarCheck } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/nucleo/utilitarios';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { PageHeader } from '@/ui/PageHeader';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { Spinner } from '@/ui/Spinner';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Textarea } from '@/ui/base/textarea';

import {
  MESES_ABREVIADOS,
  diasDoMes,
  feriadosDoTexto,
  indexarPorDreEMes,
  rotuloDaDre,
  useDiasLetivosDoAno,
  useDres,
  useGravarDiasLetivos,
  type Dre,
  type DiasLetivosDoMes,
} from './dados';

/**
 * `/admin/parametros/dias-letivos` — o calendário escolar oficial, em grade de
 * DRE por mês. É a base do cálculo diário da apuração mensal: o valor de um dia
 * sai do valor mensal dividido pelos dias letivos daquela DRE naquele mês.
 *
 * A célula abre o editor e "Salvar" grava de verdade: `PUT
 * bff/v1/admin/dias-letivos/:dreId/:mes` cria o mês ou altera o que já existe,
 * com a versão lida, e a grade é lida de novo em seguida. As regras (total
 * dentro do mês, feriados sem repetição) vêm do banco; a mensagem de recusa
 * aparece no próprio editor, que continua aberto.
 *
 * A grade rola na horizontal em tela estreita, com a coluna da DRE fixa à
 * esquerda — 13 linhas por 12 meses não cabem em 375 px, e a regra é não ter
 * rolagem horizontal na *página* (regra 4 das regras de interface), não dentro
 * de um contêiner que a pede.
 */
export function DiasLetivosPage() {
  const anoAtual = new Date().getFullYear();
  const [ano, setAno] = useState(anoAtual);
  const [celulaEmEdicao, setCelulaEmEdicao] = useState<{ dre: Dre; mes: number } | null>(null);

  const dres = useDres();
  const dias = useDiasLetivosDoAno(ano);

  const indice = useMemo(() => indexarPorDreEMes(dias.data?.itens), [dias.data]);

  const carregando = dres.isLoading || dias.isLoading;
  const comErro = dres.isError || dias.isError;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dias letivos"
        description="Calendário escolar oficial por DRE e mês. Alimenta o cálculo do valor diário na apuração mensal."
      >
        <Select value={String(ano)} onValueChange={(valor) => setAno(Number(valor))}>
          <SelectTrigger className="w-[120px]" aria-label="Ano do calendário">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[anoAtual - 1, anoAtual, anoAtual + 1].map((item) => (
              <SelectItem key={item} value={String(item)}>
                {item}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Grade DRE por mês — {ano}</CardTitle>
          <CardDescription>
            Escolha uma célula para ver e ajustar o total de dias, os feriados e as observações
            daquele mês.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {comErro ? (
            <ErrorState
              variant="load-failed"
              description="Não foi possível carregar o calendário letivo agora. Verifique sua conexão e tente novamente."
              primaryAction={{
                label: 'Tentar de novo',
                onClick: () => {
                  void dres.refetch();
                  void dias.refetch();
                },
              }}
            />
          ) : carregando ? (
            <Spinner rotulo="Carregando o calendário letivo" />
          ) : !dres.data || dres.data.length === 0 ? (
            <EmptyState
              icon={CalendarCheck}
              title="Nenhuma DRE cadastrada"
              description="Sem Diretoria Regional de Educação não há grade de dias letivos para montar."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <caption className="sr-only">
                  Total de dias letivos por Diretoria Regional de Educação em cada mês de {ano}
                </caption>
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th
                      scope="col"
                      className="sticky left-0 bg-muted/50 px-3 py-2 text-left font-medium text-muted-foreground"
                    >
                      DRE
                    </th>
                    {MESES_ABREVIADOS.map((mes) => (
                      <th
                        key={mes}
                        scope="col"
                        className="min-w-[56px] px-2 py-2 text-center font-medium text-muted-foreground"
                      >
                        {mes}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dres.data.map((dre) => (
                    <tr key={dre.id} className="border-b last:border-0">
                      <th
                        scope="row"
                        className="sticky left-0 whitespace-nowrap bg-background px-3 py-2 text-left font-medium"
                      >
                        <abbr title={dre.nome ?? undefined} className="no-underline">
                          {rotuloDaDre(dre)}
                        </abbr>
                      </th>
                      {MESES_ABREVIADOS.map((nomeDoMes, indiceDoMes) => {
                        const registro = indice[`${dre.id}-${indiceDoMes}`];
                        const total = registro?.totalDiasLetivos;
                        return (
                          <td key={nomeDoMes} className="p-0 text-center">
                            <button
                              type="button"
                              onClick={() => setCelulaEmEdicao({ dre, mes: indiceDoMes })}
                              className={cn(
                                'w-full rounded px-2 py-2 transition-colors',
                                'hover:bg-primary/10 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring',
                                total != null ? 'font-medium' : 'text-muted-foreground',
                              )}
                              aria-label={`${dre.nome ?? rotuloDaDre(dre)}, ${nomeDoMes} de ${ano}: ${
                                total != null ? `${total} dias letivos` : 'sem registro'
                              }`}
                            >
                              {total ?? '—'}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {celulaEmEdicao && (
        <EditorDaCelula
          ano={ano}
          dre={celulaEmEdicao.dre}
          mes={celulaEmEdicao.mes}
          registro={indice[`${celulaEmEdicao.dre.id}-${celulaEmEdicao.mes}`]}
          onFechar={() => setCelulaEmEdicao(null)}
        />
      )}
    </div>
  );
}

interface EditorDaCelulaProps {
  ano: number;
  dre: Dre;
  mes: number;
  registro: DiasLetivosDoMes | undefined;
  onFechar: () => void;
}

function EditorDaCelula({ ano, dre, mes, registro, onFechar }: EditorDaCelulaProps) {
  const gravar = useGravarDiasLetivos();
  const limite = diasDoMes(ano, mes);
  // Com datas informadas, o total é o número delas: o banco recusa outro valor.
  const totalSegueAsDatas = (registro?.datasLetivas.length ?? 0) > 0;
  const [total, setTotal] = useState(String(registro?.totalDiasLetivos ?? 0));
  const [feriados, setFeriados] = useState((registro?.feriados ?? []).join(', '));
  const [observacoes, setObservacoes] = useState(registro?.observacoes ?? '');
  const [aviso, setAviso] = useState<string | null>(null);

  function salvar() {
    const numero = Number(total);
    if (total.trim() === '' || !Number.isInteger(numero) || numero < 0 || numero > limite) {
      setAviso(`O total de dias letivos precisa ficar entre 0 e ${limite}, os dias deste mês.`);
      return;
    }
    setAviso(null);
    gravar.mutate(
      {
        dreId: dre.id,
        ano,
        mes,
        entrada: {
          totalDiasLetivos: numero,
          feriados: feriadosDoTexto(feriados),
          observacoes: observacoes.trim() === '' ? null : observacoes.trim(),
          ...(registro ? { versao: registro.versao } : {}),
        },
      },
      {
        onSuccess: () => {
          toast.success('Dias letivos salvos', {
            description: `${rotuloDaDre(dre)} — ${MESES_ABREVIADOS[mes]}/${ano}.`,
          });
          onFechar();
        },
        // A mensagem da API já diz o que fazer; o editor continua aberto.
        onError: (erro) => setAviso(erro.aviso),
      },
    );
  }

  return (
    <ResponsiveModal
      open
      onOpenChange={(aberto) => !aberto && onFechar()}
      title={`${rotuloDaDre(dre)} — ${MESES_ABREVIADOS[mes]}/${ano}`}
      description={dre.nome ?? undefined}
      desktopMaxWidth="max-w-md"
      footer={
        <>
          <Button variant="outline" onClick={onFechar} disabled={gravar.isPending}>
            Cancelar
          </Button>
          <Button onClick={salvar} disabled={gravar.isPending}>
            {gravar.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 py-2">
        {aviso && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{aviso}</AlertDescription>
          </Alert>
        )}

        <div className="grid gap-2">
          <Label htmlFor="dias-total">Total de dias letivos</Label>
          <Input
            id="dias-total"
            type="number"
            min={0}
            max={limite}
            value={total}
            readOnly={totalSegueAsDatas}
            onChange={(evento) => setTotal(evento.target.value)}
            className="w-32"
          />
          <p className="text-xs text-muted-foreground">
            {totalSegueAsDatas
              ? 'Este mês tem datas letivas informadas, e o total é o número delas.'
              : 'É este número que divide o valor mensal para chegar ao valor de um dia de operação.'}
          </p>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="dias-feriados">Feriados e suspensões</Label>
          <Input
            id="dias-feriados"
            value={feriados}
            onChange={(evento) => setFeriados(evento.target.value)}
            placeholder="Carnaval, Corpus Christi"
          />
          <p className="text-xs text-muted-foreground">Separe por vírgula, sem repetir o nome.</p>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="dias-observacoes">Observações</Label>
          <Textarea
            id="dias-observacoes"
            rows={3}
            maxLength={1000}
            value={observacoes}
            onChange={(evento) => setObservacoes(evento.target.value)}
            placeholder="Justificativa de uma alteração fora do calendário oficial, por exemplo."
          />
        </div>
      </div>
    </ResponsiveModal>
  );
}

export default DiasLetivosPage;
